import { Directive, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { PopoverProperties } from './interfaces';
import { EventService as OverlayEventService } from './overlay/event.service';
import { Overlay } from './overlay/overlay.service';
import { Popover as PopoverService } from './popover.service';

@Directive({ selector: '[popoverTriggerFor], [popover]' })
export class PopoverTriggerDirective {
  hideTimeoutId: number;
  destroyTimeoutId: number;
  createTimeoutId: number;
  showTimeoutId: number;
  globalEventsSubscription;

  get componentRef() {
    return this.overlay.componentRefs[0];
  }

  get isPopoverDestroyed() {
    return this.componentRef && this.componentRef.hostView.destroyed;
  }

  @Input() popoverTriggerFor: any;
  @Input() popover: any;

  @Input() popoverClass: PopoverProperties['popoverClass'];
  @Input() hideDelay: PopoverProperties['hideDelay'];
  @Input() showDelay: PopoverProperties['showDelay'];
  @Input() trigger: PopoverProperties['trigger'] = 'click';
  @Input() pointerEvents: PopoverProperties['pointerEvents'];
  @Input() display: PopoverProperties['display'] = true;

  // Animation
  @Input() animation: PopoverProperties['animation'];
  @Input() animationDuration: PopoverProperties['animationDuration'];
  @Input() animationTimingFunction: PopoverProperties['animationTimingFunction'];
  @Input() animationTranslateY: PopoverProperties['animationTranslateY'];

  // Position
  @Input() placement: PopoverProperties['placement'];
  @Input() autoPlacement: PopoverProperties['autoPlacement'];
  @Input() position: PopoverProperties['position'];
  @Input() zIndex: PopoverProperties['zIndex'];

  // Sizes
  @Input() width: PopoverProperties['width'];
  @Input() height: PopoverProperties['height'];
  @Input() maxWidth: PopoverProperties['maxWidth'];
  @Input() minWidth: PopoverProperties['minWidth'];
  @Input() whiteSpace: PopoverProperties['whiteSpace'];

  // Styles
  @Input() theme: PopoverProperties['theme'];
  @Input() offset: PopoverProperties['offset'];
  @Input() padding: PopoverProperties['padding'];
  @Input() noArrow: PopoverProperties['noArrow'];
  @Input() borderRadius: PopoverProperties['borderRadius'];
  @Input() shadow: PopoverProperties['shadow'];
  @Input() fontSize: PopoverProperties['fontSize'];

  // Custom
  @Input() isMobile: PopoverProperties['isMobile'];
  @Input() closeOnScroll: PopoverProperties['closeOnScroll'];
  @Input() closeOnRotate: PopoverProperties['closeOnRotate'];

  @Output() events: EventEmitter<any> = new EventEmitter<any>();

  @HostListener('click', ['$event'])
  onClick(event: any) {
    if (this.trigger != 'click' || !this.display) {
      return;
    }

    this.load(event);
  }

  @HostListener('focusin', ['$event'])
  @HostListener('mouseenter', ['$event'])
  onMouseEnter(event: any) {
    if (this.trigger != 'hover' || !this.display) {
      return;
    }

    if (this.popoverService.checkEventForUniqueness(event)) {
      this.load(event);
    } else {
      if (!this.componentRef || this.isPopoverDestroyed) {
        this.load(event);
      } else if (!this.isPopoverDestroyed) {
        this.popoverService.clearTimeouts(event);
      }
    }
  }

  @HostListener('focusout')
  @HostListener('mouseleave')
  onMouseLeave() {
    if (this.trigger != 'hover') {
      return;
    }

    this.popoverService.destroyPopover();
  }

  constructor(
    private elementRef: ElementRef,
    public popoverService: PopoverService,
    public overlay: Overlay,
    private overlayEventService: OverlayEventService
  ) {
    this.globalEventsSubscription = this.overlayEventService.emitter.subscribe((event) => {
      this.handleGlobalEvents(event);
    });
  }

  ngOnDestroy() {
    this.popoverService.close();
  }

  load(event: any) {
    this.popoverService.load({
      event,
      element: this.getPopoverElement(),
      popoverText: this.popover,
      width: this.width,
      height: this.height,
      maxWidth: this.maxWidth,
      minWidth: this.minWidth,
      placement: this.placement,
      autoPlacement: this.autoPlacement,
      popoverClass: this.popoverClass,
      theme: this.theme,
      offset: this.offset,
      animationDuration: this.animationDuration,
      animationTimingFunction: this.animationTimingFunction,
      animationTranslateY: this.animationTranslateY,
      padding: this.padding,
      zIndex: this.zIndex,
      noArrow: this.noArrow,
      hideDelay: this.hideDelay,
      showDelay: this.showDelay,
      trigger: this.trigger,
      position: this.position,
      whiteSpace: this.whiteSpace,
      borderRadius: this.borderRadius,
      animation: this.animation,
      shadow: this.shadow,
      pointerEvents: this.pointerEvents,
      fontSize: this.fontSize,
      isMobile: this.isMobile,
      closeOnScroll: this.closeOnScroll,
      closeOnRotate: this.closeOnRotate,
    });
  }

  getPopoverElement() {
    if (typeof this.popover === 'string') {
      return undefined;
    }

    let element = this.popoverTriggerFor;

    if (this.popoverTriggerFor.elementRef) {
      element = this.popoverTriggerFor.elementRef.nativeElement;
    }

    return element;
  }

  handleGlobalEvents(event: any) {
    if (event.type === 'Show' || event.type === 'Shown' || event.type === 'Hide' || event.type === 'Hidden') {
      this.events.emit(event);
    }
  }
}
